"use client"

import * as React from "react"
import { format } from "date-fns"
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface TimePickerProps {
  value: string
  onChange: (time: string) => void
  hourMode?: 12 | 24
}

function TimePickerSelect({
  value,
  onChange,
  hourMode = 24,
}: TimePickerProps) {
  const [hours, minutes, seconds] = value.split(":").map(Number)

  const hours12 = Array.from({ length: 12 }, (_, i) => i + 1)
  const hours24 = Array.from({ length: 24 }, (_, i) => i)
  const minutesSecs = Array.from({ length: 60 }, (_, i) => i)

  const displayHour = hourMode === 12 ? (hours % 12 || 12) : hours
  const isPM = hourMode === 12 && hours >= 12

  const handleHoursChange = (h: number) => {
    let newHours = h
    if (hourMode === 12) {
      newHours = isPM ? (h % 12) + 12 : h % 12
      if (h === 12) newHours = isPM ? 12 : 0
    }
    const newTime = `${String(newHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
    onChange(newTime)
  }

  const handleMinutesChange = (m: number) => {
    const newTime = `${String(hours).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
    onChange(newTime)
  }

  const handleSecondsChange = (s: number) => {
    const newTime = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    onChange(newTime)
  }

  const togglePeriod = () => {
    const newHours = isPM ? hours - 12 : hours + 12
    const newTime = `${String(newHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
    onChange(newTime)
  }

  const hourList = hourMode === 12 ? hours12 : hours24

  return (
    <div className="flex items-center gap-1">
      <select
        value={displayHour}
        onChange={(e) => handleHoursChange(parseInt(e.target.value))}
        className="h-9 w-14 rounded-md border border-input bg-background px-1.5 text-center text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer"
      >
        {hourList.map((h) => (
          <option key={h} value={h}>
            {String(h).padStart(2, "0")}
          </option>
        ))}
      </select>
      <span className="text-muted-foreground font-medium text-sm">:</span>
      <select
        value={minutes}
        onChange={(e) => handleMinutesChange(parseInt(e.target.value))}
        className="h-9 w-14 rounded-md border border-input bg-background px-1.5 text-center text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer"
      >
        {minutesSecs.map((m) => (
          <option key={m} value={m}>
            {String(m).padStart(2, "0")}
          </option>
        ))}
      </select>
      <span className="text-muted-foreground font-medium text-sm">:</span>
      <select
        value={seconds}
        onChange={(e) => handleSecondsChange(parseInt(e.target.value))}
        className="h-9 w-14 rounded-md border border-input bg-background px-1.5 text-center text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer"
      >
        {minutesSecs.map((s) => (
          <option key={s} value={s}>
            {String(s).padStart(2, "0")}
          </option>
        ))}
      </select>
      {hourMode === 12 && (
        <Button
          variant={isPM ? "default" : "outline"}
          size="sm"
          onClick={togglePeriod}
          className={cn("ml-1 h-9 w-12 px-1", isPM && "bg-primary hover:bg-primary-hover")}
        >
          {isPM ? "PM" : "AM"}
        </Button>
      )}
    </div>
  )
}

interface CalendarGridProps {
  selectedDate: Date | undefined
  onSelect: (date: Date) => void
  currentMonth: Date
  onMonthChange: (month: Date) => void
}

function CalendarGrid({ selectedDate, onSelect, currentMonth, onMonthChange }: CalendarGridProps) {
  const weekDays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1)
  const lastDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0)
  const startDay = firstDayOfMonth.getDay()
  const daysInMonth = lastDayOfMonth.getDate()

  const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
  const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)

  const days: (Date | null)[] = []
  for (let i = 0; i < startDay; i++) {
    days.push(null)
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i))
  }

  const isToday = (date: Date) => {
    const today = new Date()
    return date.toDateString() === today.toDateString()
  }

  const isSelected = (date: Date) => {
    return selectedDate?.toDateString() === date.toDateString()
  }

  return (
    <div className="p-3">
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => onMonthChange(prevMonth)}
          className="h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 hover:bg-accent rounded-md flex items-center justify-center"
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </button>
        <span className="text-sm font-medium">
          {format(currentMonth, "MMMM yyyy")}
        </span>
        <button
          type="button"
          onClick={() => onMonthChange(nextMonth)}
          className="h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 hover:bg-accent rounded-md flex items-center justify-center"
        >
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekDays.map((day) => (
          <div
            key={day}
            className="text-muted-foreground rounded-md w-9 font-normal text-xs py-2 text-center"
          >
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map((date, index) => (
          <div key={index} className="h-9 w-9 text-center text-sm p-0 relative">
            {date && (
              <button
                type="button"
                onClick={() => onSelect(date)}
                className={cn(
                  "h-9 w-9 p-0 font-normal rounded-md flex items-center justify-center transition-colors",
                  isSelected(date) && "bg-primary text-primary-foreground hover:bg-primary",
                  isToday(date) && !isSelected(date) && "bg-accent text-accent-foreground",
                  !isSelected(date) && !isToday(date) && "hover:bg-accent"
                )}
              >
                {date.getDate()}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

interface DateTimePickerProps {
  value?: Date
  onChange?: (date: Date) => void
  placeholder?: string
  className?: string
  hourMode?: 12 | 24
}

export function DateTimePicker({
  value,
  onChange,
  placeholder = "Select date and time",
  className,
  hourMode = 24,
}: DateTimePickerProps) {
  const [open, setOpen] = React.useState(false)
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(value)
  const [selectedTime, setSelectedTime] = React.useState(
    value ? format(value, "HH:mm:ss") : "10:30:00"
  )
  const [currentMonth, setCurrentMonth] = React.useState(
    value ? new Date(value.getFullYear(), value.getMonth(), 1) : new Date()
  )

  React.useEffect(() => {
    if (value) {
      setSelectedDate(value)
      setSelectedTime(format(value, "HH:mm:ss"))
      setCurrentMonth(new Date(value.getFullYear(), value.getMonth(), 1))
    }
  }, [value])

  const handleDateSelect = (date: Date) => {
    const [hours, minutes, seconds] = selectedTime.split(":").map(Number)
    const newDate = new Date(date)
    newDate.setHours(hours || 0, minutes || 0, seconds || 0)
    setSelectedDate(newDate)
    onChange?.(newDate)
  }

  const handleTimeChange = (time: string) => {
    setSelectedTime(time)
    if (selectedDate) {
      const [hours, minutes, seconds] = time.split(":").map(Number)
      const newDate = new Date(selectedDate)
      newDate.setHours(hours || 0, minutes || 0, seconds || 0)
      onChange?.(newDate)
    }
  }

  const handleClear = () => {
    setSelectedDate(undefined)
    setSelectedTime("10:30:00")
    setOpen(false)
  }

  const displayValue = selectedDate
    ? format(selectedDate, "PPP") +
      " " +
      format(selectedDate, hourMode === 12 ? "hh:mm:ss a" : "HH:mm:ss")
    : ""

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "justify-between font-normal h-10 px-3 min-w-[240px]",
            !selectedDate && "text-muted-foreground",
            className
          )}
        >
          <span>{displayValue || placeholder}</span>
          <ChevronDownIcon className="h-4 w-4 text-muted-foreground ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex flex-row">
          <div className="border-r border-border">
            <CalendarGrid
              selectedDate={selectedDate}
              onSelect={handleDateSelect}
              currentMonth={currentMonth}
              onMonthChange={setCurrentMonth}
            />
          </div>
          <div className="p-4 flex flex-col gap-3">
            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground uppercase">Time</span>
              <TimePickerSelect
                value={selectedTime}
                onChange={handleTimeChange}
                hourMode={hourMode}
              />
            </div>
            <div className="flex gap-2 mt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleClear}
                className="flex-1"
              >
                Clear
              </Button>
              <Button
                size="sm"
                onClick={() => setOpen(false)}
                className="flex-1"
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}